import Button from "components/form/Button";
import InputField from "components/form/InputField";
import InputSelect from "components/form/InputSelect";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { useEffect, useState, useCallback, ChangeEvent, useRef } from "react";
import { useAppDispatch } from "store/hooks";
import {
  countryListGet,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import { countryAndState } from "store/slices/documentsSlice";
import { EditData, ErrorMessage } from "types/UserProfile";
import { CountryOption, StateOption } from "types/common";

// Shared by this form's on-blur check, its Save guard, and the parent's
// server-side guard, so all three report a missing field the same way.
export const ADDRESS_REQUIRED_FIELDS = [
  ["country", "Country is required"],
  ["address1", "Address line 1 is required"],
  ["city", "City is required"],
  ["postal_code", "ZIP code is required"],
  ["state", "State is required"],
] as const;

interface AddressFormProps {
  city?: string;
  address1?: string;
  address2?: string;
  country?: string;
  state?: string;
  postal_code?: string;
  handleCancel: () => void;
  handleSave: (data: Partial<EditData>) => void;
  errMsg: ErrorMessage;
  setErrMsg: (err: ErrorMessage) => void;
  addressLoading?: boolean;
}

const AddressForm = (props: AddressFormProps) => {
  const {
    city,
    address1,
    address2,
    country,
    state,
    handleCancel,
    handleSave,
    errMsg,
    setErrMsg,
    addressLoading,
    postal_code,
  } = props;

  const [editData, setEditData] = useState<Partial<EditData>>({
    city,
    address1,
    address2,
    country,
    state,
    postal_code,
  });

  const prevCountryRef = useRef<string | undefined>(country);

  // Every edit clears the validation message, so a corrected field stops showing
  // an error that no longer applies.
  const updateField = (
    data: Partial<EditData> | ((prev: Partial<EditData>) => Partial<EditData>)
  ) => {
    setEditData((state) => ({
      ...(state || {}),
      ...(typeof data === "function" ? data(state || {}) : data),
    }));
    if (errMsg.flag) {
      setErrMsg({ flag: false, message: null, index: -1 });
    }
  };

  const isBlank = (field?: string) => !field || field.trim() === "";

  const hasMissingRequiredField = ADDRESS_REQUIRED_FIELDS.some(([field]) =>
    isBlank(editData?.[field])
  );

  // Leaving a required box empty explains why Save is unavailable.
  const handleRequiredBlur = (
    field: (typeof ADDRESS_REQUIRED_FIELDS)[number][0]
  ) => {
    if (!isBlank(editData?.[field])) return;
    const entry = ADDRESS_REQUIRED_FIELDS.find(([name]) => name === field);
    if (entry) setErrMsg({ flag: true, message: entry[1], index: 5 });
  };

  const countryList = useCountryList();
  const dispatch = useAppDispatch();
  const onCountryListGet = async () => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      console.log("conntry list get err: -->", error?.message);
    }
  };

  const [states, setStates] = useState<StateOption[]>([]);
  const [selectedState, setSelectedState] = useState<StateOption | undefined>(
    undefined
  );
  const [countriesWithStates, setCountriesWithStates] = useState<Array<any>>(
    []
  );

  useEffect(() => {
    const getCountryAndState = async () => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = res?.body?.data || [];
        setCountriesWithStates(data);
      } catch (error) {
        console.error(error);
      }
    };
    getCountryAndState();
  }, []);

  const getStatesForCountryCode = useCallback(
    (code: string) => {
      try {
        const countryMeta = countryList?.find(
          (c: CountryOption) => c?.value === code
        );
        const countryName = countryMeta?.name;
        if (!countryName) return [] as StateOption[];
        const found = countriesWithStates?.find(
          (c: any) =>
            c?.country_name?.toLowerCase() === countryName?.toLowerCase()
        );
        const st = found?.states || [];
        return st.map((s: any) => ({
          name: s?.state_name,
          value: s?.state_name,
          state_code: s?.state_code,
        }));
      } catch (e) {
        return [] as StateOption[];
      }
    },
    [countryList, countriesWithStates]
  );

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
    }
  }, [countryList]);

  useEffect(() => {
    if (countryList && country) {
      const found = countryList.find(
        (obj: CountryOption) => obj.value === country
      );
      if (found) {
        setEditData((prev: Partial<EditData>) => {
          if (prev.country === found.value) return prev;
          // Only clear state if country actually changed from a different value
          const countryChanged =
            prevCountryRef.current && prevCountryRef.current !== country;
          prevCountryRef.current = country;
          return {
            ...prev,
            country: found.value,
            state: countryChanged ? "" : prev.state,
          };
        });
      }
    }
  }, [country]);

  useEffect(() => {
    const countryName = countryList.find(
      (obj: CountryOption) => obj.value === country
    );
    if (countryName && countriesWithStates?.length > 0) {
      const nStates = getStatesForCountryCode(countryName.value as string);
      if (nStates?.length > 0 && state) {
        const selectedStateObject = nStates.find(
          (x: StateOption) =>
            x.value === state || x.name === state || x.state_code === state
        );
        setSelectedState(selectedStateObject);
        setEditData((prev) => ({
          ...(prev || {}),
          state: selectedStateObject ? selectedStateObject.value : "",
        }));
      }
    }
  }, [
    state,
    country,
    countriesWithStates,
    countryList,
    getStatesForCountryCode,
  ]);

  useEffect(() => {
    if (
      editData?.country &&
      countriesWithStates?.length > 0 &&
      countryList?.length > 0
    ) {
      const nStates = getStatesForCountryCode(editData.country as string);
      setStates(nStates);
    }
  }, [
    editData?.country,
    countriesWithStates,
    countryList,
    getStatesForCountryCode,
  ]);

  useEffect(() => {
    if (states?.length > 0 && editData?.state) {
      const selected = states.find(
        (x) =>
          x.value === editData.state ||
          x.name === editData.state ||
          x.state_code === editData.state
      );
      if (selected) setSelectedState(selected);
    }
  }, [states, editData?.state]);

  const countryReady = countryList?.length > 0;

  return (
    <div className="flex flex-col gap-[12px] w-full">
      {countryReady ? (
        <InputSelect
          isSearchFeature={false}
          label={""}
          className={"!py-[6px] !px-[12px]"}
          arrowClassName={"!top-3"}
          placeholder={"Country"}
          options={countryList}
          selected={editData?.country}
          setSelected={(val: CountryOption | null) => {
            if (val?.value) {
              const nStates = getStatesForCountryCode(val.value as string);
              setStates(nStates);
              setSelectedState(undefined);
              let country = editData?.country;

              updateField((prev) => ({
                country: val.value,
                state: val.value === country ? prev?.state : "",
              }));
            }
          }}
        />
      ) : null}
      <InputField
        name="address1"
        label=""
        className="!py-[6px] !px-[12px]"
        _className="!text-xl !font-medium !leading-normal"
        placeholder="Enter address line 1"
        value={editData.address1 || ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          updateField({ address1: e?.target?.value })
        }
        onBlur={() => handleRequiredBlur("address1")}
      />
      <InputField
        name="address2"
        label=""
        className="!py-[6px] !px-[12px]"
        _className="!text-xl !font-medium !leading-normal"
        placeholder="Enter address line 2"
        value={editData.address2 || ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          updateField({ address2: e?.target?.value })
        }
      />
      <InputField
        name="zipCode"
        type="number"
        label=""
        className="!py-[6px] !px-[12px]"
        _className="!text-xl !font-medium !leading-normal"
        placeholder="Enter ZIP code"
        value={editData.postal_code || ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          updateField({ postal_code: e?.target?.value })
        }
        onBlur={() => handleRequiredBlur("postal_code")}
      />
      <InputField
        name="city"
        label=""
        className="!py-[6px] !px-[12px]"
        _className="!text-xl !font-medium !leading-normal"
        placeholder="Enter city"
        value={editData.city || ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          updateField({ city: e?.target?.value })
        }
        onBlur={() => handleRequiredBlur("city")}
      />

      {countryReady && (
        states?.length > 0 ? (
          <InputSelect
            isSearchFeature={false}
            label={""}
            className={"!py-[6px] !px-[12px]"}
            arrowClassName={"!top-3"}
            placeholder={"State"}
            selected={selectedState?.value ?? ""}
            options={states}
            setSelected={(e: StateOption | null) => {
              if (e?.value) {
                updateField({ state: e.value });
                setSelectedState(e);
              }
            }}
          />
        ) : (
          <InputField
            name="state"
            label=""
            className="!py-[6px] !px-[12px]"
            _className="!text-xl !font-medium !leading-normal"
            placeholder="Enter state"
            value={editData.state || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateField({ state: e?.target?.value })
            }
            onBlur={() => handleRequiredBlur("state")}
          />
        )
      )}

      {errMsg.flag && errMsg.index === 5 && (
        <div className="text-sm text-red flex justify-start gap-[5px]">
          <InfoRedCircleIcon />
          {errMsg.message}
        </div>
      )}

      <div className="flex gap-[20px] mt-[8px]">
        <Button
          text={"Save"}
          isLoading={addressLoading}
          onClick={() => {
            setErrMsg({ flag: false, message: null, index: -1 });
            const stateCode = states?.find(
              (s) => s.value === editData.state
            )?.state_code;
            let payload = { ...editData };
            if (stateCode) {
              payload = { ...payload, state: stateCode };
            }
            handleSave(payload || {});
          }}
          isDisabled={addressLoading || hasMissingRequiredField}
        ></Button>
        <Button
          isDisabled={addressLoading}
          text={"Cancel"}
          variant={"white"}
          onClick={() => {
            handleCancel();
          }}
        ></Button>
      </div>
    </div>
  );
};

export default AddressForm;
